import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Eye, IdCard, X } from 'lucide-react'
import {
  DOCUMENT_REVIEW_STATUSES,
  ID_DOCUMENT_TYPES,
  ID_SCAN_REQUIRED,
  SCAN_STATUSES,
  formatFileSize,
  type IdDocumentType,
  type IdScanInfo,
} from '@concours/shared'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DocumentPreviewModal,
  Textarea,
  useAuth,
  useToast,
  type BadgeVariant,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { APPLICATIONS_KEY } from './ApplicationsPage'

const REVIEW_VARIANT: Record<IdScanInfo['reviewStatus'], BadgeVariant> = {
  pending: 'neutral',
  accepted: 'success',
  rejected: 'danger',
}

const SCAN_VARIANT: Record<IdScanInfo['scanStatus'], BadgeVariant> = {
  pending: 'warning',
  clean: 'success',
  infected: 'danger',
  error: 'danger',
}

/**
 * Copie de la pièce d'identité du candidat (profil, figée au premier dépôt) :
 * recto et verso de la carte d'identité, ou page d'identité du passeport.
 * 🔒 Chaque consultation est journalisée (`candidate.id_scan_viewed`).
 */
export function IdentityScansCard({
  applicationId,
  documentType,
  scans,
  canReview = false,
}: {
  applicationId: string
  documentType: IdDocumentType | null
  scans: IdScanInfo[]
  /** Contrôle de conformité (recevabilité) : accepter / refuser avec motif. */
  canReview?: boolean
}) {
  const [open, setOpen] = useState<IdScanInfo | null>(null)
  const sides = documentType ? ID_SCAN_REQUIRED[documentType] : []

  return (
    <Card>
      <CardHeader
        title="Pièce d’identité"
        actions={
          documentType && <Badge variant="neutral">{ID_DOCUMENT_TYPES[documentType].label}</Badge>
        }
      />
      <CardBody>
        {!documentType ? (
          <p className="text-muted doc-review-empty">Type de pièce non renseigné.</p>
        ) : (
          <ul className="doc-review-list">
            {sides.map((side) => {
              const scan = scans.find((s) => s.side === side)
              return (
                <li key={side}>
                  <div className="doc-review-head">
                    <IdCard aria-hidden="true" size={18} />
                    <strong>
                      {side === 'front' ? 'Recto' : side === 'back' ? 'Verso' : 'Page d’identité'}
                    </strong>
                    <span className="text-muted">obligatoire</span>
                  </div>
                  {scan ? (
                    <div className="doc-review-row" data-current>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <p className="doc-name">{scan.name}</p>
                        <p className="doc-meta">
                          {formatFileSize(scan.size)} · déposée le {when(scan.uploadedAt)}
                        </p>
                        <p className="doc-meta">
                          <Badge variant={SCAN_VARIANT[scan.scanStatus]} dot>
                            {SCAN_STATUSES[scan.scanStatus]}
                          </Badge>
                          <Badge variant={REVIEW_VARIANT[scan.reviewStatus]} dot>
                            {DOCUMENT_REVIEW_STATUSES[scan.reviewStatus]}
                          </Badge>
                          {scan.reviewReason && <span>Motif : {scan.reviewReason}</span>}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Eye />}
                        disabled={!scan.available}
                        onClick={() => setOpen(scan)}
                        aria-label={`Consulter la pièce d’identité : ${scan.sideLabel}`}
                      >
                        Consulter
                      </Button>
                    </div>
                  ) : (
                    <p className="text-muted doc-review-empty">Copie non fournie.</p>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        <p
          className="text-muted"
          style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--text-sm)' }}
        >
          Copie déposée par le candidat dans son profil, figée depuis son premier dépôt. Chaque
          consultation est enregistrée au journal d’audit.
        </p>
      </CardBody>
      {open && (
        <ScanModal
          applicationId={applicationId}
          scan={open}
          canReview={canReview}
          onClose={() => setOpen(null)}
        />
      )}
    </Card>
  )
}

function ScanModal({
  applicationId,
  scan,
  canReview,
  onClose,
}: {
  applicationId: string
  scan: IdScanInfo
  canReview: boolean
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState(scan.reviewReason ?? '')
  const url = `/admin/applications/${applicationId}/identity-scans/${scan.id}`
  // Chaque ouverture = une consultation journalisée (pas de cache).
  const file = useQuery({
    queryKey: ['admin', 'identity-scan-file', scan.id],
    queryFn: () =>
      session.api.download(`${url}/file`, { query: { inline: 1 } }).then((r) => r.blob),
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
  })
  const review = useMutation({
    mutationFn: (decision: 'accepted' | 'rejected') =>
      session.api.post(`${url}/review`, { decision, reason: reason.trim() || null }),
    onSuccess: async (_res, decision) => {
      toast.success(decision === 'accepted' ? 'Copie acceptée' : 'Copie refusée')
      await queryClient.invalidateQueries({ queryKey: [...APPLICATIONS_KEY, applicationId] })
      onClose()
    },
    onError: (error) => {
      if (!fieldErrors(error).reason) toast.error(errorMessage(error))
    },
  })
  return (
    <DocumentPreviewModal
      open
      onClose={onClose}
      title={`Pièce d’identité — ${scan.sideLabel}`}
      description={`${scan.name} · ${formatFileSize(scan.size)} · SHA-256 ${scan.sha256.slice(0, 12)}…`}
      blob={file.data ?? null}
      error={file.isError ? errorMessage(file.error) : undefined}
      fileName={scan.name}
      actions={
        canReview && (
          <>
            <Button
              variant="danger"
              icon={<X />}
              loading={review.isPending && review.variables === 'rejected'}
              disabled={reason.trim().length < 10}
              onClick={() => review.mutate('rejected')}
            >
              Refuser
            </Button>
            <Button
              variant="primary"
              icon={<Check />}
              loading={review.isPending && review.variables === 'accepted'}
              onClick={() => review.mutate('accepted')}
            >
              Accepter
            </Button>
          </>
        )
      }
    >
      {canReview && (
        <Textarea
          label="Motif (obligatoire pour un refus)"
          rows={2}
          hint="10 caractères minimum. Le motif est communiqué au candidat, qui pourra déposer une nouvelle copie."
          value={reason}
          error={fieldErrors(review.error).reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}
    </DocumentPreviewModal>
  )
}
